import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/api';
import { validators } from '../utils/validation';
import { toast } from './ui/sonner';
import PasswordConfirmModal from './PasswordConfirmModal';
import UserActivityDrawer from './UserActivityDrawer';
import {
  Users, UserPlus, RefreshCw, Search, KeyRound, Archive, Trash2,
  Mail, Lock, Copy, Check, X, Loader2, AlertCircle, ShieldCheck,
  ChevronLeft, ChevronRight, Phone, Send, Crown, ShieldAlert, Activity,
  AlertTriangle, ArrowLeftRight,
} from 'lucide-react';

const ROLE_OPTIONS_ADMIN = [
  // In admin mode the Create flow is strictly for administrators.
  // Workers / employers / agents are created from their own onboarding flows
  // and managed under the existing "All Users" tab.
  { id: 'admin', label: 'Admin', icon: <ShieldCheck className="w-3.5 h-3.5" /> },
  { id: 'super_admin', label: 'Super Admin', icon: <Crown className="w-3.5 h-3.5" /> },
];

const ROLE_PILL = {
  super_admin: 'bg-rose-50 text-rose-700 border-rose-200',
  admin: 'bg-violet-50 text-violet-700 border-violet-200',
  employer: 'bg-blue-50 text-blue-700 border-blue-200',
  agent: 'bg-teal-50 text-teal-700 border-teal-200',
  worker: 'bg-amber-50 text-amber-700 border-amber-200',
};

const ROLE_LABEL = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  employer: 'Employer',
  agent: 'Agent',
  worker: 'Worker',
};

const effectiveRole = (u) => (u?.role === 'admin' && u?.is_super_admin) ? 'super_admin' : (u?.role || '');

/**
 * Unified User Management panel.
 *
 * Props:
 *  - mode: "admin" | "employer"   (employer covers agent too)
 *  - currentUser: the logged-in user object
 */
export default function UserManagementPanel({ mode = 'admin', currentUser }) {
  const isAdmin = mode === 'admin';
  const isEmployerLike = !isAdmin;
  const isSuper = !!currentUser?.is_super_admin;
  // In admin mode the Create flow is strictly for administrators.
  //   super_admin → can create Admin OR Super Admin
  //   regular admin → has no creation rights here at all (button hidden below)
  const adminCreateRoles = isSuper
    ? ROLE_OPTIONS_ADMIN                                  // Admin + Super Admin
    : [];
  const allowedRoles = isAdmin
    ? adminCreateRoles
    : [{ id: 'worker', label: 'Worker' }];
  const canCreate = allowedRoles.length > 0;

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  // In admin mode this panel is dedicated to administrators only (admin + super_admin),
  // matching the user's spec that workers/employers/agents live under "All Users".
  const [roleFilter, setRoleFilter] = useState(isAdmin ? 'admin-only' : 'all');
  const PAGE_SIZE = 25;

  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [roleTarget, setRoleTarget] = useState(null);
  const [activityTarget, setActivityTarget] = useState(null);

  const listEndpoint = isAdmin ? '/api/admin/users' : '/api/employer/workers/roster';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('skip', String(skip));
      params.set('limit', String(PAGE_SIZE));
      if (q) params.set('q', q);
      if (isAdmin) {
        // Default admin tab → administrators only (admin role).
        // Super admins are also stored with role=admin so this catches both.
        if (roleFilter === 'admin-only' || roleFilter === 'all') {
          params.set('role', 'admin');
        } else if (roleFilter !== 'all') {
          params.set('role', roleFilter);
        }
      }
      const res = await apiCall(`${listEndpoint}?${params.toString()}`);
      const list = res.users || res.workers || [];
      setUsers(list);
      setTotal(res.total || list.length);
    } catch (e) {
      toast.error(e?.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [skip, q, roleFilter, isAdmin, listEndpoint]);

  useEffect(() => { load(); }, [load]);

  const onSearch = (e) => {
    e?.preventDefault?.();
    setSkip(0);
    setQ(qInput.trim());
  };

  // ---- Delete (admin → any user, employer → can't delete from here) ----
  const handleDelete = async ({ password, reason }) => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await apiCall(`/api/admin/users/${deleteTarget._id}`, {
        method: 'DELETE',
        body: JSON.stringify({ password, reason }),
      });
      toast.success('User permanently deleted');
      setDeleteTarget(null);
      load();
    } catch (e) {
      setDeleteError(e?.message || 'Failed to delete');
    } finally {
      setDeleteBusy(false);
    }
  };

  // ---- Archive ----
  const handleArchive = async (target) => {
    try {
      await apiCall(`/api/admin/users/${target._id}/archive`, { method: 'POST' });
      toast.success('User archived');
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed to archive');
    }
  };
  const toggleWorkerAccess = async (target) => {
    const grant = !(target.roles || []).includes('worker');
    const preverified = grant && window.confirm(`Mark ${target.full_name || target.email} as pre-verified trusted staff (skip identity verification in the Worker portal)?\n\nOK = pre-verified · Cancel = they must complete verification`);
    try {
      await apiCall(`/api/admin/users/${target._id}/roles`, { method: 'POST', body: JSON.stringify({ role: 'worker', grant, preverified }) });
      toast.success(grant ? `${target.full_name || target.email} can now also work as a Worker${preverified ? ' (pre-verified)' : ''}` : 'Worker access removed');
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed to update roles');
    }
  };

  const handleUnarchive = async (target) => {
    try {
      await apiCall(`/api/admin/users/${target._id}/unarchive`, { method: 'POST' });
      toast.success('User restored');
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed to restore');
    }
  };

  return (
    <div className="space-y-5" data-testid="user-management-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            User Management
          </h2>
          <p className="text-slate-500 mt-1">
            {isAdmin
              ? 'Manage administrator accounts (Admin + Super Admin). Workers, employers and agents live in the All Users tab.'
              : 'Add workers to your roster, reset their passwords if they get locked out, and manage access.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-700 text-sm font-medium hover:bg-slate-50 transition"
            data-testid="user-mgmt-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {canCreate && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition shadow-sm"
              data-testid="user-mgmt-create-btn"
            >
              <UserPlus className="w-4 h-4" />
              {isAdmin ? 'Create administrator' : 'Add worker'}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {isAdmin && (
          <div className="inline-flex items-center gap-1.5 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700">
            <ShieldAlert className="w-3.5 h-3.5" />
            <span>
              <strong>Administrators only.</strong> Creating users here is restricted to Admin and Super Admin accounts. Workers, employers and agents are in <em>All Users</em>.
            </span>
          </div>
        )}
        <form onSubmit={onSearch} className="flex items-center gap-2 flex-1">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search by name, email or phone…"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="user-mgmt-search-input"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
            data-testid="user-mgmt-search-btn"
          >
            Search
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name / Email</th>
                {isAdmin && <th className="text-left px-4 py-3 font-semibold">Role</th>}
                <th className="text-left px-4 py-3 font-semibold">Phone</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && users.length === 0 && (
                <tr><td colSpan={isAdmin ? 5 : 4} className="px-4 py-12 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 5 : 4} className="px-4 py-12 text-center text-slate-500">
                    <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    No users found.
                  </td>
                </tr>
              )}
              {users.map((u) => {
                const archived = u.is_archived || u.is_active === false;
                return (
                  <tr key={u._id} className="hover:bg-slate-50 transition" data-testid={`user-row-${u._id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        {u.suspicious_24h >= 5 && (
                          <span
                            className="mt-1 inline-flex items-center justify-center w-2 h-2 rounded-full bg-red-500 ring-2 ring-red-200 flex-shrink-0"
                            data-testid={`suspicious-dot-${u._id}`}
                            title={`${u.suspicious_24h} failed login/password attempts in the last 24h`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-slate-900 truncate max-w-[230px]">{u.full_name || '—'}</span>
                            {u.suspicious_24h >= 5 && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border bg-red-50 text-red-700 border-red-200"
                                data-testid={`suspicious-badge-${u._id}`}
                                title={`${u.suspicious_24h} failed authentication attempts in the last 24h. Investigate.`}
                              >
                                <AlertTriangle className="w-3 h-3" />
                                {u.suspicious_24h} fails / 24h
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 truncate max-w-[260px]">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        {(() => {
                          const eff = effectiveRole(u);
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${ROLE_PILL[eff] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                              {eff === 'super_admin' && <Crown className="w-3 h-3" />}
                              {ROLE_LABEL[eff] || eff}
                            </span>
                          );
                        })()}
                        {(u.roles || []).filter((r) => r !== u.role).map((r) => (
                          <span key={r} className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid={`user-extra-role-${u._id}-${r}`}>
                            +{ROLE_LABEL[r] || r}
                          </span>
                        ))}
                      </td>
                    )}
                    <td className="px-4 py-3 text-slate-600">{u.phone || '—'}</td>
                    <td className="px-4 py-3">
                      {archived ? (
                        u.auto_lockout_at ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border bg-red-50 text-red-700 border-red-200"
                            title={u.auto_lockout_reason || 'Auto-locked due to suspicious activity'}
                            data-testid={`auto-locked-${u._id}`}
                          >
                            🔒 auto-locked
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border bg-amber-50 text-amber-700 border-amber-200">
                            archived
                          </span>
                        )
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
                          active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <button
                          onClick={() => setActivityTarget(u)}
                          className="px-2.5 py-1.5 bg-slate-100 text-slate-700 border border-slate-200 rounded-md text-xs font-semibold flex items-center gap-1 hover:bg-slate-200 transition"
                          data-testid={`user-activity-${u._id}`}
                          title="View activity timeline"
                        >
                          <Activity className="w-3.5 h-3.5" /> Activity
                        </button>
                        <button
                          onClick={() => setResetTarget(u)}
                          className="px-2.5 py-1.5 bg-violet-50 text-violet-700 border border-violet-200 rounded-md text-xs font-semibold flex items-center gap-1 hover:bg-violet-100 transition"
                          data-testid={`user-reset-${u._id}`}
                          title="Reset password"
                        >
                          <KeyRound className="w-3.5 h-3.5" /> Reset
                        </button>
                        {isAdmin && u.role !== 'worker' && (
                          <button
                            onClick={() => toggleWorkerAccess(u)}
                            className={`px-2.5 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 transition border ${
                              (u.roles || []).includes('worker')
                                ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                                : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                            }`}
                            data-testid={`user-worker-access-${u._id}`}
                            title={(u.roles || []).includes('worker') ? 'Remove Worker access' : 'Also allow this user to work shifts as a Worker'}
                          >
                            <ArrowLeftRight className="w-3.5 h-3.5" /> {(u.roles || []).includes('worker') ? 'Worker ✓' : '+ Worker'}
                          </button>
                        )}
                        {isAdmin && isSuper && (
                          <button
                            onClick={() => setRoleTarget(u)}
                            disabled={currentUser && u._id === currentUser._id}
                            title={currentUser && u._id === currentUser._id ? "You can't change your own role" : 'Change role / permissions'}
                            className="px-2.5 py-1.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-md text-xs font-semibold flex items-center gap-1 hover:bg-rose-100 transition disabled:opacity-50"
                            data-testid={`user-role-${u._id}`}
                          >
                            <Crown className="w-3.5 h-3.5" /> Role
                          </button>
                        )}
                        {isAdmin && (
                          <>
                            {archived ? (
                              <button
                                onClick={() => handleUnarchive(u)}
                                className="px-2.5 py-1.5 bg-slate-700 text-white rounded-md text-xs font-semibold flex items-center gap-1 hover:bg-slate-800 transition"
                                data-testid={`user-restore-${u._id}`}
                              >
                                Restore
                              </button>
                            ) : (
                              <button
                                onClick={() => setArchiveTarget(u)}
                                className="px-2.5 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-md text-xs font-semibold flex items-center gap-1 hover:bg-amber-100 transition"
                                data-testid={`user-archive-${u._id}`}
                              >
                                <Archive className="w-3.5 h-3.5" /> Archive
                              </button>
                            )}
                            <button
                              onClick={() => { setDeleteError(''); setDeleteTarget(u); }}
                              className="px-2.5 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-md text-xs font-semibold flex items-center gap-1 hover:bg-red-100 transition disabled:opacity-50"
                              disabled={currentUser && u._id === currentUser._id}
                              title={currentUser && u._id === currentUser._id ? "You can't delete your own account" : 'Delete user'}
                              data-testid={`user-delete-${u._id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50 text-sm">
          <span className="text-slate-600">
            {total === 0 ? '0' : `${skip + 1}–${Math.min(skip + users.length, total)}`} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              disabled={skip === 0 || loading}
              className="px-2 py-1.5 rounded-lg text-slate-600 hover:bg-slate-200 disabled:opacity-40"
              data-testid="user-mgmt-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setSkip(skip + PAGE_SIZE)}
              disabled={skip + PAGE_SIZE >= total || loading}
              className="px-2 py-1.5 rounded-lg text-slate-600 hover:bg-slate-200 disabled:opacity-40"
              data-testid="user-mgmt-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <CreateUserModal
          mode={mode}
          allowedRoles={allowedRoles}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {/* Reset modal */}
      {resetTarget && (
        <ResetPasswordModal
          target={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={() => { setResetTarget(null); load(); }}
        />
      )}

      {/* Archive confirm */}
      <PasswordConfirmModal
        open={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={async () => { await handleArchive(archiveTarget); setArchiveTarget(null); }}
        title={`Archive "${archiveTarget?.full_name || archiveTarget?.email || ''}"?`}
        description="The user will lose login access. You can restore them later from this same panel."
        confirmLabel="Archive user"
        tone="warning"
        requireReason={false}
      />

      {/* Delete confirm */}
      <PasswordConfirmModal
        open={!!deleteTarget}
        onClose={() => { if (!deleteBusy) { setDeleteTarget(null); setDeleteError(''); } }}
        onConfirm={handleDelete}
        busy={deleteBusy}
        error={deleteError}
        requireReason
        title={`Permanently delete "${deleteTarget?.full_name || deleteTarget?.email || ''}"?`}
        description="This will erase the user account and cannot be undone. Their historical data (jobs, audit logs) is preserved for compliance."
        confirmLabel="Delete Permanently"
        tone="danger"
      />

      {/* Role change modal */}
      {roleTarget && (
        <ChangeRoleModal
          target={roleTarget}
          onClose={() => setRoleTarget(null)}
          onDone={() => { setRoleTarget(null); load(); }}
        />
      )}

      {/* Activity timeline drawer */}
      {activityTarget && (
        <UserActivityDrawer
          userId={activityTarget._id}
          onClose={() => setActivityTarget(null)}
        />
      )}
    </div>
  );
}

// =====================================================================
// CREATE USER MODAL
// =====================================================================
function CreateUserModal({ mode, allowedRoles, onClose, onCreated }) {
  const isAdmin = mode === 'admin';
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState(allowedRoles[0]?.id || 'worker');
  const [method, setMethod] = useState('password'); // 'password' | 'invite'
  const [dualRole, setDualRole] = useState(false);
  const [preverified, setPreverified] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { user, temp_password }
  const [copied, setCopied] = useState(false);

  const generate = () => {
    const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    const specials = '!@#$%&*';
    let p = '';
    for (let i = 0; i < 11; i++) p += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
    p += specials.charAt(Math.floor(Math.random() * specials.length));
    setPassword(p);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    const emailErr = validators.required(email) || validators.email(email);
    if (emailErr) {
      setError(emailErr === 'This field is required.' ? 'Please enter a valid email' : emailErr);
      return;
    }
    const nameErr = fullName.trim() ? validators.name(fullName) : null;
    if (nameErr) {
      setError(nameErr);
      return;
    }
    const phoneErr = validators.phone(phone);
    if (phoneErr) {
      setError(phoneErr);
      return;
    }
    setBusy(true);
    try {
      const url = isAdmin ? '/api/admin/users' : '/api/employer/workers/create';
      const body = {
        email: email.trim(),
        full_name: fullName.trim(),
        phone: phone.trim(),
      };
      if (isAdmin) body.role = role;
      if (dualRole) {
        if (isAdmin) { body.also_worker = true; if (preverified) body.preverified = true; }
        else body.also_hr = true;
      }
      if (method === 'password') {
        const pwErr = validators.required(password) || validators.strongPassword(password);
        if (pwErr) {
          setError(pwErr);
          setBusy(false);
          return;
        }
        body.password = password;
      } else {
        body.send_invite = true;
      }
      const res = await apiCall(url, { method: 'POST', body: JSON.stringify(body) });
      setResult(res);
      toast.success(res.message || 'User created');
    } catch (e) {
      setError(e?.message || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const copyPw = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  if (result) {
    return (
      <ModalShell onClose={onCreated} title="Account created" testid="create-user-result">
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-emerald-900">
                {result.user.full_name || result.user.email} can now sign in
              </div>
              <div className="text-emerald-700 text-xs mt-0.5">
                Email: <span className="font-mono">{result.user.email}</span> · Role: <span className="capitalize">{result.user.role}</span>
              </div>
            </div>
          </div>
          {result.temp_password ? (
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                Temporary password (system-generated)
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-slate-100 rounded-lg text-sm font-mono text-slate-800 select-all">
                  {result.temp_password}
                </code>
                <button
                  onClick={() => copyPw(result.temp_password)}
                  className="px-3 py-2 bg-slate-800 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-900 transition"
                  data-testid="copy-temp-password"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {result.emailed
                  ? 'This password has also been emailed to the user.'
                  : 'Share this securely — it will not be shown again. The user should change it on first login.'}
              </p>
            </div>
          ) : (
            <div className="text-sm text-slate-600 px-3 py-2 bg-slate-50 rounded-lg">
              The password you entered has been set. Make sure the user knows it.
            </div>
          )}
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
          <button
            onClick={onCreated}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
            data-testid="create-user-done"
          >
            Done
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell
      onClose={onClose}
      title={isAdmin ? 'Create a new user' : 'Add a worker to your roster'}
      testid="create-user-modal"
    >
      <form onSubmit={submit} className="px-6 py-5 space-y-4">
        <FieldRow label="Full name">
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Jane Doe"
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
            data-testid="create-user-name"
          />
        </FieldRow>
        <FieldRow label="Email" required>
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com"
              className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
              data-testid="create-user-email"
            />
          </div>
        </FieldRow>
        <FieldRow label="Phone">
          <div className="relative">
            <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 123 4567"
              className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="create-user-phone"
            />
          </div>
        </FieldRow>

        {isAdmin && (
          <FieldRow label="Role" required>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {allowedRoles.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRole(r.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold border transition flex items-center justify-center gap-1.5 ${
                    role === r.id
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`create-user-role-${r.id}`}
                >
                  {r.id === 'super_admin' && <Crown className="w-3.5 h-3.5" />}
                  {r.label || ROLE_LABEL[r.id]}
                </button>
              ))}
            </div>
          </FieldRow>
        )}

        <label className="flex items-start gap-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50/60 cursor-pointer" data-testid="create-user-dual-role">
          <input
            type="checkbox"
            checked={dualRole}
            onChange={(e) => setDualRole(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-indigo-600"
            data-testid="create-user-dual-role-checkbox"
          />
          <span className="text-sm text-slate-800">
            <span className="font-semibold">{isAdmin ? 'Also works shifts as a Worker' : 'Also an HR administrator (HRIS access)'}</span>
            <span className="block text-xs text-slate-500 mt-0.5">
              {isAdmin
                ? 'The account gets a "Switch to Worker" button and can pick up and work shifts like any worker.'
                : 'The worker also gets full access to your HR portal and can switch between Worker and HR from the header.'}
            </span>
          </span>
        </label>
        {dualRole && isAdmin && (
          <label className="flex items-start gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50/60 cursor-pointer" data-testid="create-user-preverified">
            <input
              type="checkbox"
              checked={preverified}
              onChange={(e) => setPreverified(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-emerald-600"
              data-testid="create-user-preverified-checkbox"
            />
            <span className="text-sm text-slate-800">
              <span className="font-semibold">Trusted staff — mark as pre-verified</span>
              <span className="block text-xs text-slate-500 mt-0.5">Skips the identity-verification step in the Worker portal.</span>
            </span>
          </label>
        )}

        <FieldRow label="Onboarding method">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMethod('password')}
              className={`px-3 py-2.5 rounded-lg text-sm font-semibold border flex items-center justify-center gap-2 transition ${
                method === 'password'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="create-user-method-password"
            >
              <Lock className="w-4 h-4" /> Set password
            </button>
            <button
              type="button"
              onClick={() => setMethod('invite')}
              className={`px-3 py-2.5 rounded-lg text-sm font-semibold border flex items-center justify-center gap-2 transition ${
                method === 'invite'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="create-user-method-invite"
            >
              <Send className="w-4 h-4" /> Email invite
            </button>
          </div>
        </FieldRow>

        {method === 'password' && (
          <FieldRow label="Password" required>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono"
                  data-testid="create-user-password"
                />
              </div>
              <button
                type="button"
                onClick={generate}
                className="px-3 py-2.5 bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-200 transition"
                data-testid="create-user-generate-pw"
              >
                Generate
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              The user can change this from their profile once they sign in.
            </p>
          </FieldRow>
        )}
        {method === 'invite' && (
          <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
            We'll generate a secure temporary password and email it to the user. They can change it on first login.
          </div>
        )}

        {error && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}
      </form>

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 rounded-lg transition disabled:opacity-50"
          data-testid="create-user-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
          data-testid="create-user-submit"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {isAdmin ? 'Create user' : 'Add worker'}
        </button>
      </div>
    </ModalShell>
  );
}

// =====================================================================
// RESET PASSWORD MODAL
// =====================================================================
function ResetPasswordModal({ target, onClose, onDone }) {
  const [method, setMethod] = useState('password'); // 'password' | 'email'
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = () => {
    const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let p = '';
    for (let i = 0; i < 12; i++) p += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
    setNewPassword(p);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!confirmPassword.trim()) {
      setError('Please enter your password to confirm this action');
      return;
    }
    if (method === 'password' && (!newPassword || newPassword.length < 6)) {
      setError('New password must be at least 6 characters');
      return;
    }
    setBusy(true);
    try {
      const body = { confirm_password: confirmPassword };
      if (method === 'password') {
        body.new_password = newPassword;
        body.send_email = false;
      } else {
        body.send_email = true; // server generates and emails
      }
      const res = await apiCall(`/api/users/${target._id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setResult(res);
      toast.success(res.message || 'Password reset');
    } catch (e) {
      setError(e?.message || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  const copyPw = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  if (result) {
    return (
      <ModalShell onClose={onDone} title="Password reset" testid="reset-result">
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-emerald-900">{target.full_name || target.email}'s password is updated</div>
              <div className="text-emerald-700 text-xs mt-0.5">
                {result.emailed ? 'They have been emailed with the new password.' : 'They have not been emailed — share the password securely.'}
              </div>
            </div>
          </div>
          {result.temp_password && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                New password
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-slate-100 rounded-lg text-sm font-mono text-slate-800 select-all">
                  {result.temp_password}
                </code>
                <button
                  onClick={() => copyPw(result.temp_password)}
                  className="px-3 py-2 bg-slate-800 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-900 transition"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
          <button
            onClick={onDone}
            className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700"
          >
            Done
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell
      onClose={onClose}
      title={`Reset password for ${target.full_name || target.email}`}
      testid="reset-modal"
    >
      <form onSubmit={submit} className="px-6 py-5 space-y-4">
        <FieldRow label="Reset method">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMethod('password')}
              className={`px-3 py-2.5 rounded-lg text-sm font-semibold border flex items-center justify-center gap-2 transition ${
                method === 'password'
                  ? 'bg-violet-600 text-white border-violet-600'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="reset-method-password"
            >
              <Lock className="w-4 h-4" /> Set new password
            </button>
            <button
              type="button"
              onClick={() => setMethod('email')}
              className={`px-3 py-2.5 rounded-lg text-sm font-semibold border flex items-center justify-center gap-2 transition ${
                method === 'email'
                  ? 'bg-violet-600 text-white border-violet-600'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="reset-method-email"
            >
              <Send className="w-4 h-4" /> Email a new one
            </button>
          </div>
        </FieldRow>

        {method === 'password' && (
          <FieldRow label="New password" required>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 font-mono"
                  data-testid="reset-new-password"
                />
              </div>
              <button
                type="button"
                onClick={generate}
                className="px-3 py-2.5 bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-200 transition"
                data-testid="reset-generate-pw"
              >
                Generate
              </button>
            </div>
          </FieldRow>
        )}
        {method === 'email' && (
          <div className="px-3 py-2 bg-violet-50 border border-violet-200 rounded-lg text-xs text-violet-700">
            We'll generate a secure password and email it to <strong>{target.email}</strong>.
          </div>
        )}

        <FieldRow label="Confirm your password" required>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Your current password"
              className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400"
              autoComplete="current-password"
              required
              data-testid="reset-confirm-password"
            />
          </div>
        </FieldRow>

        {error && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}
      </form>
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 rounded-lg transition disabled:opacity-50"
          data-testid="reset-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 transition disabled:opacity-50 flex items-center gap-2"
          data-testid="reset-submit"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Reset password
        </button>
      </div>
    </ModalShell>
  );
}

// =====================================================================
// CHANGE ROLE MODAL (super_admin only)
// =====================================================================
function ChangeRoleModal({ target, onClose, onDone }) {
  const currentRole = effectiveRole(target);
  const ROLES = [
    { id: 'worker', label: 'Worker', desc: 'Standard worker — apply to jobs, get matched.' },
    { id: 'employer', label: 'Employer', desc: 'Post jobs, manage their own workers.' },
    { id: 'agent', label: 'Agent', desc: 'Agency — manages a roster of workers.' },
    { id: 'admin', label: 'Admin', desc: 'Moderate users, jobs, and view audit trail.' },
    { id: 'super_admin', label: 'Super Admin', desc: 'Full control — manage admins, grant/revoke roles.' },
  ];
  const [newRole, setNewRole] = useState(currentRole === 'super_admin' ? 'admin' : 'super_admin');
  const [reason, setReason] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!confirmPassword.trim()) {
      setError('Your password is required');
      return;
    }
    if (newRole === currentRole) {
      setError('Pick a different role to make a change');
      return;
    }
    setBusy(true);
    try {
      await apiCall(`/api/admin/users/${target._id}/role`, {
        method: 'POST',
        body: JSON.stringify({
          new_role: newRole,
          confirm_password: confirmPassword,
          reason: reason.trim() || undefined,
        }),
      });
      toast.success(`Role changed: ${currentRole} → ${newRole}`);
      onDone();
    } catch (e) {
      setError(e?.message || 'Role change failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      onClose={onClose}
      title={`Change role for ${target.full_name || target.email}`}
      testid="role-modal"
    >
      <form onSubmit={submit} className="px-6 py-5 space-y-4">
        <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
          <span className="text-slate-500">Current role:</span>{' '}
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${ROLE_PILL[currentRole] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
            {currentRole === 'super_admin' && <Crown className="w-3 h-3" />}
            {ROLE_LABEL[currentRole] || currentRole}
          </span>
        </div>

        <FieldRow label="New role" required>
          <div className="space-y-1.5">
            {ROLES.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setNewRole(r.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border transition flex items-center gap-3 ${
                  newRole === r.id
                    ? 'bg-rose-50 border-rose-300 ring-2 ring-rose-100'
                    : 'bg-white border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`role-pick-${r.id}`}
              >
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${ROLE_PILL[r.id]}`}>
                  {r.id === 'super_admin' && <Crown className="w-3 h-3" />}
                  {r.label}
                </span>
                <span className="text-xs text-slate-500 flex-1">{r.desc}</span>
                {currentRole === r.id && <span className="text-[10px] uppercase tracking-wider text-slate-400">current</span>}
              </button>
            ))}
          </div>
        </FieldRow>

        <FieldRow label="Reason (audited)">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this role being changed?"
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-400"
            data-testid="role-reason"
          />
        </FieldRow>

        <FieldRow label="Confirm your password" required>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Your current password"
              autoComplete="current-password"
              required
              className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-400"
              data-testid="role-confirm-password"
            />
          </div>
        </FieldRow>

        {error && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}
      </form>
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 rounded-lg transition disabled:opacity-50"
          data-testid="role-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || newRole === currentRole}
          className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-semibold hover:bg-rose-700 transition disabled:opacity-50 flex items-center gap-2"
          data-testid="role-submit"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Change role
        </button>
      </div>
    </ModalShell>
  );
}

// =====================================================================
// Shared bits
// =====================================================================
function ModalShell({ children, onClose, title, testid }) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 transition"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FieldRow({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}
